import type { SectionType, StepPlanInfoId, StepPlanRepetitionMode } from '@az3fr/core';
import { sectionTypes } from '@az3fr/core';
import { TrashIcon } from '@heroicons/react/24/outline';
import { memo, useCallback, useMemo } from 'react';

import type { SelectOptionConfig } from '@/lib/option-generators';
import { VirtualSelect } from '@/components/ui/virtual-select';
import { workoutCreatorThunks } from '@/features/workout-creator/model';
import { useAppDispatch } from '@/hooks/store.hook';
import { generateRepetitionOptions } from '@/lib/option-generators';

function SetNumberCellV2(props: {
	orderIndex: number;
	repetitionMode: StepPlanRepetitionMode;
	sectionType: SectionType;
	sectionRounds: number | null;
	templateSets?: number;
	stepPlanId: StepPlanInfoId;
	onDeleteSet?: () => void;
}) {
	const {
		orderIndex,
		repetitionMode,
		sectionType,
		sectionRounds,
		templateSets,
		stepPlanId,
		onDeleteSet,
	} = props;
	const dispatch = useAppDispatch();

	const handleTemplateSetChange = useCallback(
		(selectedOption: SelectOptionConfig<number> | null) => {
			if (selectedOption === null) {
				return;
			}

			dispatch(
				workoutCreatorThunks.updateStepPlanProperties({
					stepPlanId,
					changes: { templateSets: selectedOption.value },
				}),
			);
		},
		[dispatch, stepPlanId],
	);

	const showDeleteButton = sectionType === sectionTypes.STANDARD && orderIndex > 0;

	const content = useMemo(() => {
		/* Explicit mode:
		 * `Standard` section type: show order index + delete button (for order index > 0 (not the first set))
		 * `Amrap` or `Emom` or `Superset` or `Circuit` section type: only show order index
		 */
		if (repetitionMode === 'explicit') {
			return (
				<div className="flex items-center justify-center bg-gray-50">
					{/* We're using orderIndex instead of setNumber so the users can delete sets in any order without we having to re-order the sets which is a pain */}
					<span className="flex size-6 items-center justify-center rounded-full font-semibold text-gray-700 text-xs sm:size-7 sm:text-xs">
						{orderIndex + 1}
					</span>
					{showDeleteButton && (
						<button
							type="button"
							onClick={onDeleteSet}
							className="flex size-6 items-center justify-center rounded text-red-500 transition-colors hover:bg-red-50 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500/20"
							title="Delete set"
						>
							<TrashIcon className="size-3.5 sm:size-4" />
						</button>
					)}
				</div>
			);
		}

		// --- Template mode

		// Other than standard section type: show the number of rounds or null
		if (sectionType !== sectionTypes.STANDARD) {
			let displayContent: string | null = null;

			if (sectionType === sectionTypes.AMRAP || sectionType === sectionTypes.EMOM) {
				displayContent = null;
			} else if (sectionType === sectionTypes.SUPERSET || sectionType === sectionTypes.CIRCUIT) {
				displayContent = sectionRounds === null ? '-' : `${sectionRounds}x`;
			}

			return (
				<div className="flex items-center justify-center bg-gray-50 font-medium text-gray-700 text-xs sm:text-sm">
					{displayContent}
				</div>
			);
		}

		const options = generateRepetitionOptions();
		const selectedOption = options.find((option) => option.value === templateSets) ?? null;

		// Standard section type with template mode: let users choose the number of sets
		return (
			<div className="flex items-center justify-center">
				<VirtualSelect
					value={selectedOption}
					displayValue={String(templateSets ?? 0)}
					options={options}
					onChange={handleTemplateSetChange}
					enableVirtualization={true}
					disabled={false}
					buttonClassName="cursor-pointer w-full h-full font-medium text-xs sm:text-sm focus:outline-none focus:ring-2 hover:bg-gray-50 focus:ring-gray-500 focus:ring-inset flex items-center justify-center p-2.5 rounded-bl sm:p-3"
				/>
			</div>
		);
	}, [
		orderIndex,
		repetitionMode,
		sectionType,
		sectionRounds,
		templateSets,
		handleTemplateSetChange,
		showDeleteButton,
		onDeleteSet,
	]);

	return content;
}

const MemoizedSetNumberCellV2 = memo(SetNumberCellV2);

export { MemoizedSetNumberCellV2 as SetNumberCellV2 };
